// Copyright 2026 Bitech.id. Licensed under the MIT License.

import type { BiwaveTarget } from '../types.js';

// Baked in at build time from the BIWAVE_ENDPOINT repository secret (see
// tsup.config.ts's `env` option and .github/workflows/release.yml) — a
// published dist/ contains the literal production URL, not a live
// `process.env` read, so this stays safe to import in browser bundles.
// Falls back to the production URL for local dev builds where the secret
// isn't set.
export const DEFAULT_ENDPOINT = process.env.BIWAVE_ENDPOINT || 'https://biwave-ingest.bitech.id';

export interface BiwaveTargetOptions {
  clientId: string;
  endpoint?: string;
}

export function resolveTarget(options: BiwaveTargetOptions): BiwaveTarget {
  const clientId = options.clientId?.trim();
  if (!clientId) {
    throw new Error(
      'Invalid Biwave config: "clientId" is required (found in your project settings on the Biwave dashboard)',
    );
  }

  const rawEndpoint = options.endpoint?.trim() || DEFAULT_ENDPOINT;

  let url: URL;
  try {
    url = new URL(rawEndpoint);
  } catch {
    throw new Error(`Invalid Biwave config: "endpoint" is not a valid URL, got "${rawEndpoint}"`);
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new Error(
      `Invalid Biwave config: endpoint protocol must be "http" or "https", got "${url.protocol.replace(':', '')}"`,
    );
  }

  const endpoint = `${url.protocol}//${url.host}`;

  return {
    clientId,
    endpoint,
    ingestUrl: `${endpoint}/api/trace`,
    ingestLogUrl: `${endpoint}/api/log`,
    ingestPulseUrl: `${endpoint}/api/pulse`,
  };
}
